import { makeAutoObservable } from 'mobx';
import { RoutineFolder, RoutineFolderLocationUpdate } from 'hevy-shared';
import API from 'utils/API';
import { moveItemInArray } from 'utils/misc';

const ROUTINE_FOLDERS_LOCAL_STORAGE_KEY = 'ROUTINE_FOLDERS_LOCAL_STORAGE_KEY';

export class RoutineFolders {
  folders: RoutineFolder[] = [];
  folderIsCollapsedMap: Record<number, boolean> = {};

  constructor() {
    makeAutoObservable(this);
  }

  hydrate = () => {
    try {
      const json = window.localStorage.getItem(ROUTINE_FOLDERS_LOCAL_STORAGE_KEY);
      if (!json) return;

      const parsed = JSON.parse(json);

      // Only hydrate if cache has expected shape. Otherwise skip - fetch() will get fresh data.
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
      if (!Array.isArray(parsed.folders)) return;

      this.folders = parsed.folders;
      this.folderIsCollapsedMap = parsed.folderIsCollapsedMap ?? {};
    } catch {
      // Invalid cache, ignore
    }
  };

  clearData = () => {
    this.folders = [];
    this.folderIsCollapsedMap = {};
    window.localStorage.removeItem(ROUTINE_FOLDERS_LOCAL_STORAGE_KEY);
  };

  fetch = async () => {
    const result = await API.getRoutineFolders();
    this.folders = result.data;
    this.persist();
  };

  persist = () => {
    window.localStorage.setItem(
      ROUTINE_FOLDERS_LOCAL_STORAGE_KEY,
      JSON.stringify({
        folders: this.folders,
        folderIsCollapsedMap: this.folderIsCollapsedMap,
      }),
    );
  };

  updateFolderIsCollapsed = (folderId: number, isCollapsed: boolean) => {
    const folder = this.folders.find(f => f.id === folderId);
    if (!folder) return;

    this.folderIsCollapsedMap[folderId] = isCollapsed;
    this.persist();
  };

  updateFolderLocation = async (s: RoutineFolderLocationUpdate, d: RoutineFolderLocationUpdate) => {
    const orderedFolders = this.folders.slice().sort((f1, f2) => f1.index - f2.index);
    const newSortedFolders = moveItemInArray(orderedFolders, s.index, d.index);
    for (let i = 0; i < newSortedFolders.length; i++) {
      newSortedFolders[i].index = i;
    }

    this.folders = newSortedFolders;

    const newLocations: RoutineFolderLocationUpdate[] = this.folders.map(f => ({
      folderId: f.id,
      index: f.index,
    }));

    await API.updateRoutineFolderOrder(newLocations);
  };

  getFolderById = (folderId: number) => {
    return this.folders.find(f => f.id === folderId);
  };
}
